import _isEqual from 'lodash/isEqual';

import { formatMoney } from '../../utils/stringUtils';
import { isSubscriptionLine } from './lineIdentity';
import subscriptionTerms from './subscriptionTerms.json';
import type { ShoppingCartItem } from './type';

/**
 * The platform's Subscribe & Save terms, one text for every app. The checkout shows them and sends their version,
 * and the record stores it as the shopper's consent. The email templates restate the same file.
 */
export const SUBSCRIPTION_TERMS_VERSION: string = subscriptionTerms.version;
export const SUBSCRIPTION_TERMS: readonly string[] = subscriptionTerms.paragraphs;

/** How often a Subscribe & Save line is ordered. The api-checkout subscription views use the same shape. */
export interface SubscriptionFrequency {
  count: number;
  unit: 'day' | 'week' | 'month' | 'year';
  chargeDay: 'first_order' | { weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7 } | { dayOfMonth: number };
}

export type SubscribeAndSaveDiscount =
  | { type: 'percent'; bps: number }
  | { type: 'fixed_amount'; amountCents: number }
  | { type: 'fixed_price'; priceCents: number };

/**
 * The answer of `GET /api-checkout/subscribe-and-save/offer/{stacklineSku}`. Each option has its own reminder lead,
 * because a period shorter than the app's lead caps it.
 */
export interface SubscribeAndSaveOffer {
  options: { frequency: SubscriptionFrequency; nextOrderDate: string; reminderLeadDays: number }[];
  discount: SubscribeAndSaveDiscount;
  unitPriceCents: number;
  discountCents: number;
  subscriberUnitPriceCents: number;
  /** One unit's price on the next order, after one paid order. Without steps it equals `subscriberUnitPriceCents`. */
  renewalSubscriberUnitPriceCents?: number;
  /**
   * Later discounts by ascending `afterPaidOrders`, present only when the schedule has steps.
   * Each starts after that many paid orders.
   */
  discountSteps?: { afterPaidOrders: number; discount: SubscribeAndSaveDiscount; subscriberUnitPriceCents: number }[];
}

const WEEKDAYS = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];

/** The frequency inside a sentence or a select, such as "every 2 weeks, on Mondays". */
export function frequencyPhrase({ count, unit, chargeDay }: SubscriptionFrequency): string {
  const every = count === 1 ? `every ${unit}` : `every ${count} ${unit}s`;
  if (chargeDay === 'first_order') return every;
  if ('weekday' in chargeDay) return `${every}, on ${WEEKDAYS[chargeDay.weekday - 1]}`;
  return `${every}, on day ${chargeDay.dayOfMonth}`;
}

/** The frequency on its own, such as "Every 2 weeks, on Mondays". */
export function frequencyLabel(frequency: SubscriptionFrequency): string {
  const phrase = frequencyPhrase(frequency);
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

export const formatCents = (cents: number) => formatMoney(cents / 100);

export function discountLabel(discount: SubscribeAndSaveDiscount): string {
  if (discount.type === 'percent') return `${discount.bps / 100}% off`;
  if (discount.type === 'fixed_amount') return `${formatCents(discount.amountCents)} off each`;
  return `Subscriber price ${formatCents(discount.priceCents)}`;
}

/**
 * The offer's discount schedule, such as "20% off first order, then 10% off". It copies `discountScheduleLabel` of
 * pages-common, because this package does not link pages-common.
 */
export function offerDiscountLabel({ discount, discountSteps }: Pick<SubscribeAndSaveOffer, 'discount' | 'discountSteps'>): string {
  if (!discountSteps?.length) return discountLabel(discount);
  const [first, ...later] = discountSteps;
  const firstOrders = first.afterPaidOrders === 1 ? 'first order' : `first ${first.afterPaidOrders} orders`;
  return [
    `${discountLabel(discount)} ${firstOrders}`,
    `then ${discountLabel(first.discount)}`,
    ...later.map((step) => `then ${discountLabel(step.discount)} from order ${step.afterPaidOrders + 1}`),
  ].join(', ');
}

/** Formats a local `YYYY-MM-DD` date as "Nov 8" or "November 8", so no time zone moves it. */
export function formatOrderDay(date: string, month: 'short' | 'long' = 'short'): string {
  const [year, monthNumber, day] = date.split('-').map(Number);
  return new Date(year, monthNumber - 1, day).toLocaleDateString('en-US', { month, day: 'numeric' });
}

/** The offer option of a frequency. Absent when the offer no longer lists it. */
export const offerOption = (offer: SubscribeAndSaveOffer | undefined, frequency: SubscriptionFrequency) =>
  offer?.options.find((option) => _isEqual(option.frequency, frequency));

/** The offers of the bag's subscription lines, by stackline sku. */
export type SubscribeAndSaveOffers = Record<string, SubscribeAndSaveOffer | undefined>;

/**
 * The unit price the bag shows for a line. A subscription line with a known offer shows the subscriber price.
 * The checkout prices each line again, so this is for display only.
 */
export function lineUnitPrice(line: ShoppingCartItem, offers?: SubscribeAndSaveOffers): number {
  const offer = isSubscriptionLine(line) ? offers?.[line.stacklineSku] : undefined;
  return offer ? offer.subscriberUnitPriceCents / 100 : line.retailPrice;
}

export const hasSubscriptionLine = (carts: { items?: ShoppingCartItem[] }[]) =>
  carts.some((cart) => (cart.items ?? []).some(isSubscriptionLine));
